import React, { useEffect, useState, useMemo } from 'react';
import { Table, Card, DatePicker, Button, Tabs, Row, Col, Statistic, Select, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useAuth0 } from '@auth0/auth0-react';
import dayjs, { Dayjs } from 'dayjs';
import { SearchOutlined, DownloadOutlined, ArrowUpOutlined, ArrowDownOutlined, BarChartOutlined, DatabaseOutlined } from '@ant-design/icons';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import * as XLSX from 'xlsx';
import { theme } from '../../../theme';

const { Option } = Select;

// Extender dayjs con los plugins necesarios
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const { RangePicker } = DatePicker;

interface DiarioMovimientosItem {
  fecha: string;
  orden: string;
  insumo: string;
  proveedor: string;
  sector: string;
  equipo: string;
  motor: string;
  cantidad_ingreso: number;
  cantidad_egreso: number;
}

const DiarioMovimientos: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const [data, setData] = useState<DiarioMovimientosItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [selectedDateRange, setSelectedDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [filtroInsumo, setFiltroInsumo] = useState<string>('todos');
  const [filtroSector, setFiltroSector] = useState<string>('todos');

  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;

  const columns: ColumnsType<DiarioMovimientosItem> = [
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      sorter: (a, b) => a.fecha.localeCompare(b.fecha),
    },
    {
      title: 'Comprobante',
      dataIndex: 'orden',
      key: 'orden',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.orden))).map(orden => ({
        text: orden,
        value: orden,
      })),
      onFilter: (value, record) => record.orden === value,
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo',
      key: 'insumo',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.insumo))).map(insumo => ({
        text: insumo,
        value: insumo,
      })),
      onFilter: (value, record) => record.insumo === value,
    },
    {
      title: 'Proveedor/Retiro',
      dataIndex: 'proveedor',
      key: 'proveedor',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.proveedor))).map(proveedor => ({
        text: proveedor,
        value: proveedor,
      })),
      onFilter: (value, record) => record.proveedor === value,
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.sector).filter(Boolean))).map(sector => ({
        text: sector,
        value: sector,
      })),
      onFilter: (value, record) => record.sector === value,
    },
    {
      title: 'Equipo',
      dataIndex: 'equipo',
      key: 'equipo',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.equipo).filter(Boolean))).map(equipo => ({
        text: equipo,
        value: equipo,
      })),
      onFilter: (value, record) => record.equipo === value,
    },
    {
      title: 'Motor',
      dataIndex: 'motor',
      key: 'motor',
      filterSearch: true,
      filters: Array.from(new Set(data.map(item => item.motor).filter(Boolean))).map(motor => ({
        text: motor,
        value: motor,
      })),
      onFilter: (value, record) => record.motor === value,
    },
    {
      title: 'Ingreso',
      dataIndex: 'cantidad_ingreso',
      key: 'cantidad_ingreso',
      align: 'right',
    },
    {
      title: 'Egreso',
      dataIndex: 'cantidad_egreso',
      key: 'cantidad_egreso',
      align: 'right',
    },
  ];

  const fetchData = async () => {
    if (!empresa || !sucursal) {
      message.error('Faltan datos necesarios para la consulta');
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      
      // Formatear fechas para la URL
      const fechaDesde = dateRange[0].format('YYYY-MM-DD');
      const fechaHasta = dateRange[1].format('YYYY-MM-DD');
      
      const response = await fetch(
        `${process.env.REACT_APP_INSUMOS_URL}/diario_movimientos/${empresa}/${sucursal}/${fechaDesde}/${fechaHasta}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Error al obtener datos');
      }

      const responseData = await response.json() as DiarioMovimientosItem[];
      setData(responseData);
      
    } catch (error) {
      message.error('Error al cargar los datos');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresa && sucursal) {
      fetchData();
    }
  }, [empresa, sucursal, dateRange]);

  const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (dates && dates[0] && dates[1]) {
      setSelectedDateRange([dates[0], dates[1]]);
    }
  };

  const handleSearch = () => {
    setDateRange(selectedDateRange);
  };

  // Cálculo de estadísticas
  const estadisticas = useMemo(() => {
    // Filtrar datos según filtros seleccionados
    let datosFiltrados = data;
    if (filtroInsumo !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.insumo === filtroInsumo);
    }
    if (filtroSector !== 'todos') {
      datosFiltrados = datosFiltrados.filter(item => item.sector === filtroSector);
    }

    const totalRegistros = datosFiltrados.length;
    const totalIngresos = datosFiltrados.reduce((sum, item) => sum + (item.cantidad_ingreso || 0), 0);
    const totalEgresos = datosFiltrados.reduce((sum, item) => sum + (item.cantidad_egreso || 0), 0);
    const saldo = totalIngresos - totalEgresos;
    
    // Distribución por insumo
    const porInsumo = datosFiltrados.reduce((acc, item) => {
      const insumo = item.insumo || 'Sin insumo';
      if (!acc[insumo]) {
        acc[insumo] = { ingresos: 0, egresos: 0 };
      }
      acc[insumo].ingresos += item.cantidad_ingreso || 0;
      acc[insumo].egresos += item.cantidad_egreso || 0;
      return acc;
    }, {} as Record<string, { ingresos: number; egresos: number }>);
    
    const distribucionInsumo = Object.entries(porInsumo)
      .map(([insumo, datos]) => ({
        insumo,
        ingresos: datos.ingresos,
        egresos: datos.egresos,
        saldo: datos.ingresos - datos.egresos
      }))
      .sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo))
      .slice(0, 10);
    
    // Distribución por sector
    const porSector = datosFiltrados.reduce((acc, item) => {
      const sector = item.sector || 'Sin sector';
      if (!acc[sector]) {
        acc[sector] = { ingresos: 0, egresos: 0 };
      }
      acc[sector].ingresos += item.cantidad_ingreso || 0;
      acc[sector].egresos += item.cantidad_egreso || 0;
      return acc;
    }, {} as Record<string, { ingresos: number; egresos: number }>);
    
    const distribucionSector = Object.entries(porSector)
      .map(([sector, datos]) => ({
        sector,
        ingresos: datos.ingresos,
        egresos: datos.egresos,
        saldo: datos.ingresos - datos.egresos
      }))
      .sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo))
      .slice(0, 10);
    
    // Distribución por día
    const porDia = datosFiltrados.reduce((acc, item) => {
      const dia = item.fecha || 'Sin fecha';
      if (!acc[dia]) {
        acc[dia] = { ingresos: 0, egresos: 0 };
      }
      acc[dia].ingresos += item.cantidad_ingreso || 0;
      acc[dia].egresos += item.cantidad_egreso || 0;
      return acc;
    }, {} as Record<string, { ingresos: number; egresos: number }>);
    
    const distribucionDia = Object.entries(porDia)
      .map(([dia, datos]) => ({
        dia,
        ingresos: datos.ingresos,
        egresos: datos.egresos,
        saldo: datos.ingresos - datos.egresos
      }))
      .sort((a, b) => a.dia.localeCompare(b.dia))
      .slice(0, 30); // Últimos 30 días
    
    const insumosUnicos = Array.from(new Set(data.map(item => item.insumo))).sort();
    const sectoresUnicos = Array.from(new Set(data.map(item => item.sector).filter(Boolean))).sort();
    
    return {
      totalRegistros,
      totalIngresos,
      totalEgresos,
      saldo,
      distribucionInsumo,
      distribucionSector,
      distribucionDia,
      insumosUnicos,
      sectoresUnicos
    };
  }, [data, filtroInsumo, filtroSector]);

  const exportToExcel = () => {
    // Calcular totales
    const totales = data.reduce((acc, item) => ({
      ingresos: acc.ingresos + item.cantidad_ingreso,
      egresos: acc.egresos + item.cantidad_egreso
    }), { ingresos: 0, egresos: 0 });

    // Preparar los datos para Excel
    const excelData = [
      // Agregar título y fechas
      [{ v: 'DIARIO DE MOVIMIENTOS', t: 's' }],
      [{ v: `Período: ${dateRange[0].format('DD/MM/YYYY')} al ${dateRange[1].format('DD/MM/YYYY')}`, t: 's' }],
      [], // Línea en blanco
      
      // Encabezados
      ['Fecha', 'Comprobante', 'Insumo', 'Proveedor/Retiro', 'Sector', 'Equipo', 'Motor', 'Ingreso', 'Egreso'],
      
      // Datos
      ...data.map(item => [
        dayjs(item.fecha).format('DD/MM/YYYY'),
        item.orden,
        item.insumo,
        item.proveedor,
        item.sector,
        item.equipo,
        item.motor,
        Number(item.cantidad_ingreso),
        Number(item.cantidad_egreso)
      ]),
      
      [], // Línea en blanco
      
      // Totales
      ['', 'TOTALES:', '', '', '', '', '',
        Number(totales.ingresos),
        Number(totales.egresos)
      ]
    ];

    // Crear workbook y worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);

    // Configurar estilos para números
    ['H', 'I'].forEach(col => {
      for (let row = 5; row <= data.length + 4; row++) {
        const cell = ws[`${col}${row}`];
        if (cell) cell.z = '#,##0.00';
      }
    });

    // Ajustar ancho de columnas
    ws['!cols'] = [
      { width: 12 }, // Fecha
      { width: 15 }, // Comprobante
      { width: 40 }, // Insumo
      { width: 20 }, // Proveedor/Retiro
      { width: 20 }, // Sector
      { width: 20 }, // Equipo
      { width: 20 }, // Motor
      { width: 12 }, // Ingreso
      { width: 12 }  // Egreso
    ];

    // Agregar worksheet al workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Diario de Movimientos');

    // Guardar archivo
    XLSX.writeFile(wb, `Diario_Movimientos_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Diario Stock
      </h1>

      <Card 
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '100%'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600
          }
        }}
        title="Diario de Movimientos"
      >
        <div style={{ padding: '24px' }}>
          {/* Filtros */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '20px',
            marginBottom: '24px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 600,
                color: theme.colors.textPrimary,
                fontSize: '14px'
              }}>
                Rango de Fechas
              </label>
              <RangePicker
                value={selectedDateRange}
                onChange={handleDateRangeChange}
                format="DD/MM/YYYY"
                size="large"
                style={{ width: '100%' }}
              />
            </div>
            <div>
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={handleSearch}
                size="small"
                style={{
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontWeight: 500,
                  width: '100%'
                }}
              >
                Buscar
              </Button>
            </div>
            <div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={exportToExcel}
                disabled={data.length === 0}
                size="small"
                style={{
                  fontWeight: 500,
                  width: '100%',
                  opacity: data.length === 0 ? 0.6 : 1
                }}
              >
                Exportar Excel
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <Tabs
            defaultActiveKey="listado"
            size="small"
            items={[
              {
                key: 'listado',
                label: 'Listado',
                children: (
                  <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    scroll={{ x: 1000 }}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`,
                    }}
                  />
                )
              },
              {
                key: 'estadisticas',
                label: 'Estadísticas',
                children: (
                  <div style={{ padding: '20px 0' }}>
                    {/* Filtros */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Insumo:</div>
                        <Select
                          value={filtroInsumo}
                          onChange={setFiltroInsumo}
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Todos los insumos"
                        >
                          <Option value="todos">Todos</Option>
                          {estadisticas.insumosUnicos.map(ins => (
                            <Option key={ins} value={ins}>{ins}</Option>
                          ))}
                        </Select>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <div style={{ marginBottom: '8px', fontSize: '13px', fontWeight: 500 }}>Filtrar por Sector:</div>
                        <Select
                          value={filtroSector}
                          onChange={setFiltroSector}
                          size="small"
                          style={{ width: '100%' }}
                          placeholder="Todos los sectores"
                        >
                          <Option value="todos">Todos</Option>
                          {estadisticas.sectoresUnicos.map(sec => (
                            <Option key={sec} value={sec}>{sec}</Option>
                          ))}
                        </Select>
                      </Col>
                    </Row>

                    {/* Tarjetas de resumen */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total de Registros"
                            value={estadisticas.totalRegistros}
                            prefix={<DatabaseOutlined />}
                            valueStyle={{ color: theme.colors.primary }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Ingresos"
                            value={estadisticas.totalIngresos}
                            prefix={<ArrowUpOutlined />}
                            valueStyle={{ color: theme.colors.success }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Total Egresos"
                            value={estadisticas.totalEgresos}
                            prefix={<ArrowDownOutlined />}
                            valueStyle={{ color: theme.colors.error }}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} lg={6}>
                        <Card>
                          <Statistic
                            title="Saldo"
                            value={estadisticas.saldo}
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: estadisticas.saldo >= 0 ? theme.colors.success : theme.colors.error }}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Distribución por Insumo */}
                    <Card 
                      title="Top 10 - Distribución por Insumo" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionInsumo}
                        rowKey="insumo"
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Insumo',
                            dataIndex: 'insumo',
                            key: 'insumo',
                          },
                          {
                            title: 'Ingresos',
                            dataIndex: 'ingresos',
                            key: 'ingresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Egresos',
                            dataIndex: 'egresos',
                            key: 'egresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Saldo',
                            dataIndex: 'saldo',
                            key: 'saldo',
                            align: 'right' as const,
                            render: (valor: number) => (
                              <span style={{ color: valor >= 0 ? theme.colors.success : theme.colors.error }}>
                                {valor.toLocaleString('es-AR')}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </Card>

                    {/* Distribución por Sector */}
                    <Card 
                      title="Top 10 - Distribución por Sector" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionSector}
                        rowKey="sector"
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: 'Sector',
                            dataIndex: 'sector',
                            key: 'sector',
                          },
                          {
                            title: 'Ingresos',
                            dataIndex: 'ingresos',
                            key: 'ingresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Egresos',
                            dataIndex: 'egresos',
                            key: 'egresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Saldo',
                            dataIndex: 'saldo',
                            key: 'saldo',
                            align: 'right' as const,
                            render: (valor: number) => (
                              <span style={{ color: valor >= 0 ? theme.colors.success : theme.colors.error }}>
                                {valor.toLocaleString('es-AR')}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </Card>

                    {/* Distribución por Día */}
                    <Card 
                      title="Últimos 30 Días - Movimientos por Día" 
                      style={{ marginBottom: '24px' }}
                    >
                      <Table
                        dataSource={estadisticas.distribucionDia}
                        rowKey="dia"
                        pagination={false}
                        size="small"
                        scroll={{ x: 600 }}
                        columns={[
                          {
                            title: 'Fecha',
                            dataIndex: 'dia',
                            key: 'dia',
                            render: (fecha: string) => dayjs(fecha).format('DD/MM/YYYY'),
                          },
                          {
                            title: 'Ingresos',
                            dataIndex: 'ingresos',
                            key: 'ingresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Egresos',
                            dataIndex: 'egresos',
                            key: 'egresos',
                            align: 'right' as const,
                            render: (valor: number) => valor.toLocaleString('es-AR'),
                          },
                          {
                            title: 'Saldo',
                            dataIndex: 'saldo',
                            key: 'saldo',
                            align: 'right' as const,
                            render: (valor: number) => (
                              <span style={{ color: valor >= 0 ? theme.colors.success : theme.colors.error }}>
                                {valor.toLocaleString('es-AR')}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </Card>
                  </div>
                )
              }
            ]}
          />
        </div>
      </Card>
    </div>
  );
};

export default DiarioMovimientos;